import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampoDeMoeda,
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Modal,
  Selo,
  formatarMoeda,
} from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { lerValorDigitado, moedaParaCampo } from '../../servicos/formato';
import {
  carregarCreditosDaUltimaColeta,
  rotuloDoMes,
  type CreditoDaColeta,
  type CreditosDaUltimaColeta,
} from '../../servicos/prospeccao';
import { ErroDaApi } from '../ErroDaApi';

export interface Vinculo {
  readonly parceiroId: string;
  readonly fornecedorId: string;
}

export interface CreditoDoFornecedor {
  readonly tipoCredito: string;
  readonly credito: number;
  readonly desagioPercentual: number;
  readonly diaVencimento: number | null;
}

export interface Settings {
  readonly businessId: string;
  readonly papeis: { cliente: boolean; assessor: boolean; fornecedor: boolean };
  readonly vinculosComoCliente: readonly Vinculo[];
  readonly fornecedoresComoAssessor: readonly { fornecedorId: string; desagioPercentual: number }[];
  readonly creditos: readonly CreditoDoFornecedor[];
  readonly diaPagamento: number | null;
  /** RN-621 — o assessor responsável, quando a empresa é cliente. */
  readonly assessor: { readonly businessId: string; readonly razaoSocial: string } | null;
}

interface Parte {
  readonly businessId: string;
  readonly razaoSocial: string;
}

/**
 * Os tipos de crédito que o fornecedor pode ter (RN-017).
 *
 * A lista é fechada porque cada tipo **gera um contrato diferente**, com texto
 * próprio — tipo livre criaria contrato sem minuta correspondente. Os dois
 * valores que já existem na base (`PADRAO`, 46 fornecedores, e `DIFERIDO`, 2)
 * continuam aparecendo quando presentes: são cadastro real, e sumir com eles da
 * tela seria esconder o que está valendo hoje.
 */
const TIPOS_DE_CREDITO = [
  { valor: 'EXPORTACAO', rotulo: 'Exportação' },
  { valor: 'DIFERIDO', rotulo: 'Diferido' },
  { valor: 'INVESTIMENTO_TARIFACO', rotulo: 'Investimento Tarifaço' },
  { valor: 'INVESTIMENTO_FIDC_OUTROS', rotulo: 'Investimento FIDC/Outros 100%' },
] as const;

const ROTULO_LEGADO: Readonly<Record<string, string>> = { PADRAO: 'Padrão (cadastro antigo)' };

export function rotuloDoTipo(valor: string): string {
  return TIPOS_DE_CREDITO.find((t) => t.valor === valor)?.rotulo ?? ROTULO_LEGADO[valor] ?? valor;
}

interface LinhaDeCredito {
  tipoCredito: string;
  credito: string;
  desagio: string;
  diaVencimento: string;
}

/**
 * RN-015 a RN-017 — Configuração de Créditos ICMS de uma empresa.
 *
 * ## Uma empresa, três papéis possíveis
 *
 * A mesma empresa é cliente numa operação e fornecedora em outra — não são
 * cadastros diferentes, são papéis do mesmo cadastro. O que cada papel exige é
 * diferente, e por isso a tela só pede o que o papel ligado precisa:
 *
 * | Papel      | O que precisa ser definido                          |
 * | ---------- | --------------------------------------------------- |
 * | Cliente    | nada aqui — ver abaixo                              |
 * | Assessor   | de quais fornecedores ela compra                     |
 * | Fornecedor | que tipos de crédito ela vende, e em que condições   |
 *
 * ## O cliente não escolhe pares aqui
 *
 * Havia uma seção "Assessores e fornecedores do cliente". Saiu (lista de
 * 15/09, item 15): o assessor do cliente é quem o cadastrou — só o admin
 * troca —, e o fornecedor é decidido no contrato, pelo admin ou pelo assessor
 * (RN-026 grava o vínculo ao definir o fornecedor). A tela mandava a lista
 * inteira e a API substitui tudo (RN-014); por isso os vínculos existentes
 * seguem no corpo, intactos, para que salvar papéis não apague quem já opera.
 *
 * ## "Todos" não é um vínculo
 *
 * O botão de marcar todos preenche a seleção **no momento do clique** e grava a
 * lista resultante. Não guarda "todos" como estado: um fornecedor cadastrado
 * amanhã não passa a ser vínculo de quem marcou "todos" hoje — isso mudaria
 * contrato sem ninguém decidir.
 */
export function ConfiguracaoDeCredito(props: {
  readonly businessId: string;
  readonly atual: Settings;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();

  const [papeis, setPapeis] = useState(props.atual.papeis);
  const [fornecedoresDoAssessor, setFornecedoresDoAssessor] = useState<readonly string[]>([]);
  const [creditos, setCreditos] = useState<LinhaDeCredito[]>([]);
  // As linhas vieram da última coleta do SISCRED nesta abertura — a nota diz isso.
  const [preenchidaDaColeta, setPreenchidaDaColeta] = useState(false);

  // O painel não desmonta ao fechar: sem realinhar, ele reabre com o estado da
  // empresa anterior.
  useEffect(() => {
    setPapeis(props.atual.papeis);
    setFornecedoresDoAssessor(props.atual.fornecedoresComoAssessor.map((f) => f.fornecedorId));
    setCreditos(
      props.atual.creditos.map((c) => ({
        tipoCredito: c.tipoCredito,
        credito: moedaParaCampo(c.credito),
        desagio: String(c.desagioPercentual),
        diaVencimento: c.diaVencimento === null ? '' : String(c.diaVencimento),
      })),
    );
    setPreenchidaDaColeta(false);
  }, [props.atual]);

  /**
   * Os tipos de crédito e os valores da empresa na última coleta do SISCRED
   * (Prospecção). Fornecedor sem nenhum tipo cadastrado já abre com eles
   * preenchidos; quem já tem tipos vê os valores da coleta e decide atualizar.
   * Tudo continua editável antes de salvar.
   */
  const daColeta = useQuery({
    queryKey: ['crm', 'prospeccao', 'creditos-do-cadastro', props.businessId],
    queryFn: () => carregarCreditosDaUltimaColeta(props.businessId),
    enabled: props.aberto,
  });
  const creditosDaColeta = daColeta.data?.creditos ?? [];
  useEffect(() => {
    if (!props.aberto || !papeis.fornecedor || creditos.length > 0) return;
    if (creditosDaColeta.length === 0) return;
    setCreditos(creditosDaColeta.map(linhaDaColeta));
    setPreenchidaDaColeta(true);
    // Só na primeira vez: depois de preenchido, apagar as linhas é escolha de quem edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.aberto, papeis.fornecedor, daColeta.data]);

  const fornecedores = useQuery({
    queryKey: ['carteira', 'fornecedores'],
    queryFn: () => requisicaoAutenticada<Parte[]>('/icms/suppliers'),
    enabled: props.aberto,
  });

  const salvar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada('/icms/settings', {
        metodo: 'POST',
        corpo: {
          businessId: props.businessId,
          cliente: papeis.cliente,
          assessor: papeis.assessor,
          fornecedor: papeis.fornecedor,
          // Intactos: a API substitui o conjunto, e esta tela não o edita.
          assessores: papeis.cliente ? props.atual.vinculosComoCliente : [],
          fornecedores: papeis.assessor ? fornecedoresDoAssessor : [],
          creditos: papeis.fornecedor
            ? creditos
                .filter((c) => c.tipoCredito !== '')
                .map((c) => ({
                  tipoCredito: c.tipoCredito,
                  credito: lerValorDigitado(c.credito) ?? 0,
                  desagioPercentual: Number(c.desagio.replace(',', '.')) || 0,
                  ...(c.diaVencimento === '' ? {} : { diaVencimento: Number(c.diaVencimento) }),
                }))
            : [],
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings', props.businessId] });
      void queryClient.invalidateQueries({ queryKey: ['icms-papeis', props.businessId] });
      // Quem é cliente, fornecedor ou assessor mudou: as listas de escolha
      // (comprador do Pontual, cliente do Passivos) e as carteiras recarregam.
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
      props.aoFechar();
    },
  });

  const listaFornecedores = fornecedores.data ?? [];

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Configuração — Crédito ICMS"
      descricao="Os papéis valem para os três serviços de Créditos ICMS — Recorrente, Pontual e Passivos. A empresa pode ter mais de um: fornecedora no Recorrente de outras empresas e compradora no Pontual, por exemplo."
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => salvar.mutate()} carregando={salvar.isPending}>
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-conteudo-primario">Empresa habilitada como</h3>

          <Interruptor
            rotulo="Cliente"
            ajuda="Compra crédito para abater o próprio ICMS ou quitar passivo de ICMS."
            ligado={papeis.cliente}
            aoMudar={(v) => setPapeis((p) => ({ ...p, cliente: v }))}
          />
          <Interruptor
            rotulo="Assessor"
            ajuda="Intermedia a operação e ganha o spread entre o que compra e o que revende."
            ligado={papeis.assessor}
            aoMudar={(v) => setPapeis((p) => ({ ...p, assessor: v }))}
          />
          <Interruptor
            rotulo="Fornecedor"
            ajuda="Vende crédito acumulado, com deságio."
            ligado={papeis.fornecedor}
            aoMudar={(v) => setPapeis((p) => ({ ...p, fornecedor: v }))}
          />
        </section>

        {papeis.fornecedor && (
          <TiposDeCredito
            linhas={creditos}
            aoMudar={setCreditos}
            {...(daColeta.data === undefined ? {} : { daColeta: daColeta.data })}
            preenchidaDaColeta={preenchidaDaColeta}
            aoAtualizarDaColeta={() =>
              setCreditos((atuais) => atualizarComAColeta(atuais, creditosDaColeta))
            }
          />
        )}

        {papeis.assessor && (
          <SelecaoDeFornecedores
            selecionados={fornecedoresDoAssessor}
            aoMudar={setFornecedoresDoAssessor}
            fornecedores={listaFornecedores}
          />
        )}

        {!papeis.cliente && !papeis.assessor && !papeis.fornecedor && (
          <Aviso tom="atencao" titulo="Nenhum papel ligado">
            Sem papel, a empresa não aparece em carteira nenhuma e não entra em contrato. Salvar
            assim a remove da operação — o cadastro e o histórico permanecem.
          </Aviso>
        )}

        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
      </div>
    </Modal>
  );
}

/* ─── Tipos de crédito ────────────────────────────────────────────────────── */

/**
 * RN-017 — O que este fornecedor vende.
 *
 * Cada linha é um tipo de crédito com o volume habilitado, o deságio e o dia em
 * que o pagamento vence. O dia é **por linha** porque cada tipo gera um contrato
 * próprio: um fornecedor pode receber pelo diferido no dia 10 e pelo de
 * exportação no dia 25.
 */
function TiposDeCredito(props: {
  readonly linhas: readonly LinhaDeCredito[];
  readonly aoMudar: (linhas: LinhaDeCredito[]) => void;
  /** A última coleta do SISCRED da empresa, quando ela aparece em alguma. */
  readonly daColeta?: CreditosDaUltimaColeta;
  readonly preenchidaDaColeta: boolean;
  readonly aoAtualizarDaColeta: () => void;
}): JSX.Element {
  const definir = (i: number, campo: keyof LinhaDeCredito, valor: string): void => {
    props.aoMudar(props.linhas.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
  };

  const usados = new Set(props.linhas.map((l) => l.tipoCredito));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-conteudo-primario">Tipos de crédito</h3>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          onClick={() =>
            props.aoMudar([
              ...props.linhas,
              { tipoCredito: '', credito: '', desagio: '', diaVencimento: '' },
            ])
          }
        >
          + Créditos
        </Botao>
      </div>

      <AvisoDaColeta
        linhas={props.linhas}
        preenchidaDaColeta={props.preenchidaDaColeta}
        aoAtualizar={props.aoAtualizarDaColeta}
        {...(props.daColeta === undefined ? {} : { daColeta: props.daColeta })}
      />

      {props.linhas.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">
          Nenhum tipo cadastrado. Sem ao menos um, o fornecedor não tem o que vender.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {props.linhas.map((linha, i) => (
            <li
              key={i}
              className="grid gap-3 rounded-card border border-borda p-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_7rem_7rem_auto] sm:items-end"
            >
              <CampoDeSelecao
                rotulo="Tipo de crédito"
                value={linha.tipoCredito}
                onChange={(e) => definir(i, 'tipoCredito', e.target.value)}
                opcoes={[
                  { valor: '', rotulo: 'Selecione…' },
                  ...TIPOS_DE_CREDITO.filter(
                    (t) => t.valor === linha.tipoCredito || !usados.has(t.valor),
                  ).map((t) => ({ valor: t.valor, rotulo: t.rotulo })),
                  // Um tipo legado só aparece na linha que já o usa.
                  ...(linha.tipoCredito !== '' &&
                  !TIPOS_DE_CREDITO.some((t) => t.valor === linha.tipoCredito)
                    ? [{ valor: linha.tipoCredito, rotulo: rotuloDoTipo(linha.tipoCredito) }]
                    : []),
                ]}
              />
              <CampoDeMoeda
                rotulo="Qtd. créditos hab."
                value={linha.credito}
                onChange={(texto) => definir(i, 'credito', texto)}
              />
              <Campo
                rotulo="Deságio (%)"
                inputMode="decimal"
                value={linha.desagio}
                onChange={(e) => definir(i, 'desagio', e.target.value)}
              />
              <Campo
                rotulo="Dia do venc."
                type="number"
                min={1}
                max={31}
                value={linha.diaVencimento}
                onChange={(e) => definir(i, 'diaVencimento', e.target.value)}
              />
              <Botao
                variante="sutil"
                tamanho="pequeno"
                onClick={() => props.aoMudar(props.linhas.filter((_, idx) => idx !== i))}
              >
                Remover
              </Botao>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-conteudo-sutil">
        O dia do vencimento é por tipo: ele define, no contrato gerado, quando o crédito é pago ao
        fornecedor. Vazio usa o dia padrão do cadastro.
      </p>
    </section>
  );
}

/** RN-016 — de quais fornecedores este assessor compra. */
function SelecaoDeFornecedores(props: {
  readonly selecionados: readonly string[];
  readonly aoMudar: (ids: readonly string[]) => void;
  readonly fornecedores: readonly Parte[];
}): JSX.Element {
  const todosMarcados =
    props.fornecedores.length > 0 && props.selecionados.length === props.fornecedores.length;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-conteudo-primario">
          Fornecedores do assessor{' '}
          <Selo tom="neutro" tamanho="pequeno">
            {props.selecionados.length}
          </Selo>
        </h3>
        <Botao
          variante="secundario"
          tamanho="pequeno"
          onClick={() =>
            props.aoMudar(todosMarcados ? [] : props.fornecedores.map((f) => f.businessId))
          }
        >
          {todosMarcados ? 'Desmarcar todos' : 'Marcar todos'}
        </Botao>
      </div>

      <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto rounded-card border border-borda p-2">
        {props.fornecedores.map((f) => (
          <li key={f.businessId}>
            <label className="flex cursor-pointer items-center gap-3 rounded px-2 py-1.5 hover:bg-superficie-base">
              <input
                type="checkbox"
                checked={props.selecionados.includes(f.businessId)}
                onChange={(e) =>
                  props.aoMudar(
                    e.target.checked
                      ? [...props.selecionados, f.businessId]
                      : props.selecionados.filter((id) => id !== f.businessId),
                  )
                }
                className="h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano"
              />
              <span className="text-sm text-conteudo-primario">{f.razaoSocial}</span>
            </label>
          </li>
        ))}
      </ul>

      <p className="text-xs text-conteudo-sutil">
        "Marcar todos" grava a lista de agora. Um fornecedor cadastrado amanhã não entra sozinho —
        isso mudaria contrato sem ninguém decidir.
      </p>
    </section>
  );
}

/* ─── Peças ───────────────────────────────────────────────────────────────── */

function Interruptor(props: {
  readonly rotulo: string;
  readonly ajuda: string;
  readonly ligado: boolean;
  readonly aoMudar: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={props.ligado}
        onChange={(e) => props.aoMudar(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      />
      <span className="flex flex-col">
        <span className="text-sm text-conteudo-primario">{props.rotulo}</span>
        <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
      </span>
    </label>
  );
}

/* ─── Da última coleta do SISCRED ─────────────────────────────────────────── */

function linhaDaColeta(c: CreditoDaColeta): LinhaDeCredito {
  return {
    tipoCredito: c.tipoCredito,
    credito: moedaParaCampo(c.credito),
    desagio: c.desagio === null ? '' : String(c.desagio),
    diaVencimento: '',
  };
}

/**
 * Os valores da coleta nas linhas que já existem — o deságio só onde estava
 * vazio — e os tipos que faltam, no fim. Tipo cadastrado que a coleta não
 * mostra fica como está: quem decide tirá-lo é quem edita.
 */
export function atualizarComAColeta(
  linhas: readonly LinhaDeCredito[],
  daColeta: readonly CreditoDaColeta[],
): LinhaDeCredito[] {
  const atualizadas = linhas.map((l) => {
    const c = daColeta.find((d) => d.tipoCredito === l.tipoCredito);
    if (c === undefined) return l;
    return {
      ...l,
      credito: moedaParaCampo(c.credito),
      desagio: l.desagio.trim() === '' && c.desagio !== null ? String(c.desagio) : l.desagio,
    };
  });
  const faltam = daColeta.filter((c) => !linhas.some((l) => l.tipoCredito === c.tipoCredito));
  return [...atualizadas, ...faltam.map(linhaDaColeta)];
}

/** A coleta já está nas linhas: cada tipo dela existe, com o mesmo valor. */
function emDiaComAColeta(
  linhas: readonly LinhaDeCredito[],
  daColeta: readonly CreditoDaColeta[],
): boolean {
  return daColeta.every((c) =>
    linhas.some(
      (l) =>
        l.tipoCredito === c.tipoCredito &&
        Math.abs((lerValorDigitado(l.credito) ?? 0) - c.credito) < 0.005,
    ),
  );
}

function AvisoDaColeta(props: {
  readonly linhas: readonly LinhaDeCredito[];
  readonly daColeta?: CreditosDaUltimaColeta;
  readonly preenchidaDaColeta: boolean;
  readonly aoAtualizar: () => void;
}): JSX.Element | null {
  const coleta = props.daColeta?.coleta ?? null;
  const creditos = props.daColeta?.creditos ?? [];
  if (coleta === null || creditos.length === 0) return null;

  const deQuando = `da coleta do SISCRED de ${rotuloDoMes(coleta.ano, coleta.mes).toLowerCase()}${
    coleta.coletadaEm === null
      ? ''
      : ` (${new Date(`${coleta.coletadaEm}T12:00:00`).toLocaleDateString('pt-BR')})`
  }`;

  if (props.preenchidaDaColeta) {
    return (
      <Aviso tom="informacao" titulo="Preenchido com a última coleta do SISCRED">
        Os tipos e os valores habilitados vieram {deQuando}. Confira o deságio — vem da anotação da
        Prospecção, quando há — e edite o que precisar antes de salvar.
      </Aviso>
    );
  }
  if (emDiaComAColeta(props.linhas, creditos)) return null;

  return (
    <Aviso
      tom="informacao"
      titulo="A última coleta do SISCRED mostra outros valores"
      acao={
        <Botao variante="secundario" tamanho="pequeno" onClick={props.aoAtualizar}>
          Atualizar com a coleta
        </Botao>
      }
    >
      Valores {deQuando}:{' '}
      {creditos
        .map((c) => `${rotuloDoTipo(c.tipoCredito)} ${formatarMoeda(c.credito)}`)
        .join(' · ')}
      . Atualizar troca os valores dos tipos que ela mostra e acrescenta os que faltam; o deságio e
      o dia do vencimento ficam como estão.
    </Aviso>
  );
}
